import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  openInvoice,
  showBackButton,
  onBackButtonClick,
  offBackButtonClick,
  hideBackButton,
  mountBackButton,
  unmountBackButton,
} from '@telegram-apps/sdk-react';
import { BottomNav } from '../../components/design/BottomNav';
import { BottomHint } from '../../components/design/BottomHint';
import { InlineCTA } from '../../components/design/InlineCTA';
import { StepIndicator } from '../../components/design/StepIndicator';
import { brand, fontFamily, tg } from '../../theme/tokens';
import { DURATIONS, getPrice, KISS_TYPES } from '../../data/kissTypes';
import { useWizard } from '../../state/wizard';
import { createGeneration } from '../../lib/api';
import { SCENES } from '../../data/scenes';

/**
 * Step 4 — pick duration, see total price, pay.
 *
 * Layout (mockup-driven):
 *   - StepIndicator (4/4 filled)
 *   - H1 + subtitle
 *   - Recap card (2 photo thumbs + "Scene · Kiss type" + descriptor)
 *   - Duration pills (2×2 grid, each shows seconds + ⭐ price)
 *   - Error banner (if Pay failed)
 *   - InlineCTA "Pay ⭐ N" (or loader state when invoice in flight)
 *   - BottomNav
 *
 * The previous "big gold price" mid-section was removed — price now lives
 * inside the Pay CTA, so showing it twice was redundant.
 */
export function DurationStep() {
  const navigate = useNavigate();
  const { photo1, photo2, scene, kissType, durationSec, setDurationSec } = useWizard();
  const [phase, setPhase] = useState<'idle' | 'creating' | 'awaiting_payment'>('idle');
  const [error, setError] = useState<string | null>(null);

  // Photo thumbs for recap card. URL.createObjectURL leaks unless revoked,
  // so we manage them via useEffect with cleanup. Re-created only when the
  // underlying File reference changes (won't churn on every render).
  const [photo1Url, setPhoto1Url] = useState<string | null>(null);
  const [photo2Url, setPhoto2Url] = useState<string | null>(null);
  useEffect(() => {
    if (!photo1) { setPhoto1Url(null); return; }
    const u = URL.createObjectURL(photo1);
    setPhoto1Url(u);
    return () => URL.revokeObjectURL(u);
  }, [photo1]);
  useEffect(() => {
    if (!photo2) { setPhoto2Url(null); return; }
    const u = URL.createObjectURL(photo2);
    setPhoto2Url(u);
    return () => URL.revokeObjectURL(u);
  }, [photo2]);

  // Guard
  useEffect(() => {
    if (!photo1 || !photo2 || !scene || !kissType) {
      navigate('/wizard/type', { replace: true });
    }
  }, [photo1, photo2, scene, kissType, navigate]);

  // BackButton (TG native — keep)
  useEffect(() => {
    try { mountBackButton(); showBackButton(); } catch { /* */ }
    const onBack = () => navigate('/wizard/type');
    onBackButtonClick(onBack);
    return () => {
      offBackButtonClick(onBack);
      try { hideBackButton(); unmountBackButton(); } catch { /* */ }
    };
  }, [navigate]);

  const price = kissType && durationSec ? getPrice(kissType, durationSec) : null;
  const sceneObj = scene ? SCENES.find((s) => s.id === scene) : null;
  const sceneName = sceneObj?.name ?? 'Cozy Cafe';
  const kissInfo = kissType ? KISS_TYPES.find((k) => k.id === kissType) : null;

  if (!kissType) return null;

  const canPay = !!durationSec && !!price;
  const isBusy = phase !== 'idle';

  const handlePay = async () => {
    if (!canPay || !photo1 || !photo2 || isBusy) return;
    setError(null);
    setPhase('creating');

    let invoice;
    try {
      invoice = await createGeneration({
        photo1,
        photo2,
        kiss_type: kissType,
        duration_sec: durationSec!,
        // Send scene SLUG (e.g. 'cozy-cafe') not display name. Backend
        // looks up the active prompt by this slug — see prompts table
        // in 20260508120000_prompts.sql. Sending the lowercased display
        // name ('cozy cafe') causes PromptNotFoundError on the server.
        scene: scene!,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setPhase('idle');
      return;
    }

    setPhase('awaiting_payment');

    let status: string;
    try {
      status = await openInvoice(invoice.invoice_url, 'url');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not open payment');
      setPhase('idle');
      return;
    }

    if (status === 'paid') {
      // NOTE: intentionally NOT calling reset() here. Keeping photos +
      // selections in wizard state means:
      //   1. "Try different style" on Result can re-enter /wizard/scene
      //      with the same photos pre-loaded.
      //   2. If generation fails server-side, user can retry without
      //      re-uploading.
      // The reset happens explicitly when user taps "Create another kiss"
      // on ResultPage (fresh flow desired).
      navigate('/generating', { state: { id: invoice.id }, replace: true });
      return;
    }

    setError(
      status === 'cancelled'
        ? 'Payment cancelled'
        : status === 'failed'
        ? 'Payment failed — try again'
        : `Payment status: ${status}`,
    );
    setPhase('idle');
  };

  return (
    <div style={{ minHeight: '100%', display: 'flex', flexDirection: 'column', paddingBottom: 180 }}>
      <StepIndicator step={4} total={4} />

      <div style={{ padding: '18px 20px 4px', fontFamily }}>
        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 800, color: tg.text, letterSpacing: -0.5 }}>
          Set duration
        </h1>
        <div style={{ fontSize: 15, color: tg.textHint, marginTop: 4, lineHeight: 1.4 }}>
          Longer is more cinematic
        </div>
      </div>

      {/* Recap card — visual reminder of what's being purchased. Shows
          actual photo thumbs (object URLs) overlapping + scene/kiss summary. */}
      <div
        style={{
          margin: '20px 20px 0',
          padding: 14,
          borderRadius: 16,
          background: tg.bg2,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          fontFamily,
        }}
      >
        <div style={{ position: 'relative', display: 'flex', flexShrink: 0 }}>
          <RecapThumb url={photo1Url} fallbackHue={340} />
          <div style={{ marginLeft: -14 }}>
            <RecapThumb url={photo2Url} fallbackHue={20} />
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: tg.text,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {sceneName} · {kissInfo?.name ?? kissType}
          </div>
          <div style={{ fontSize: 12, color: tg.textHint, marginTop: 2 }}>
            2 photos · {kissInfo?.description?.toLowerCase() ?? 'kiss'}
          </div>
        </div>
      </div>

      {/* Duration pills — 2×2 grid */}
      <div
        style={{
          padding: '20px 20px 0',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 10,
          fontFamily,
        }}
      >
        {DURATIONS.map((d) => {
          const p = getPrice(kissType, d);
          const avail = p !== null;
          const on = d === durationSec && avail;
          return (
            <button
              key={d}
              onClick={() => avail && setDurationSec(d)}
              disabled={!avail}
              style={{
                appearance: 'none',
                padding: '14px 12px',
                borderRadius: 16,
                background: on ? `${brand.solid}10` : avail ? tg.bg2 : 'transparent',
                border: avail
                  ? on
                    ? `2px solid ${brand.solid}`
                    : '2px solid transparent'
                  : `1px dashed ${tg.divider}`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 4,
                opacity: avail ? 1 : 0.4,
                cursor: avail ? 'pointer' : 'not-allowed',
                transition: 'background 200ms, border-color 200ms',
              }}
            >
              <div
                style={{
                  fontSize: 24,
                  fontWeight: 800,
                  letterSpacing: -0.4,
                  color: tg.text,
                  lineHeight: 1,
                }}
              >
                {d}
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: tg.textHint,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  fontWeight: 600,
                }}
              >
                seconds
              </div>
              {avail ? (
                <div
                  style={{
                    marginTop: 4,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    fontSize: 13,
                    fontWeight: 700,
                    color: tg.text,
                  }}
                >
                  <span style={{ color: '#f4b23e' }}>★</span> {p}
                </div>
              ) : (
                <div style={{ fontSize: 11, color: tg.textHint, marginTop: 4 }}>
                  not available
                </div>
              )}
            </button>
          );
        })}
      </div>

      {error && (
        <div
          style={{
            margin: '16px 20px 0',
            padding: '10px 14px',
            borderRadius: 12,
            background: 'rgba(229, 72, 77, 0.08)',
            color: tg.destructive,
            fontSize: 13,
            fontFamily,
            textAlign: 'center',
          }}
        >
          {error}
        </div>
      )}

      <div style={{ flex: 1 }} />

      {canPay ? (
        <InlineCTA onClick={handlePay} loading={isBusy} disabled={isBusy}>
          {phase === 'creating'
            ? 'Preparing invoice…'
            : phase === 'awaiting_payment'
            ? 'Awaiting payment…'
            : (
              <>
                <span style={{ color: '#ffd66b', fontSize: 18, lineHeight: 1 }}>★</span>
                Pay {price} stars
              </>
            )}
        </InlineCTA>
      ) : (
        <BottomHint>Pick a duration to continue</BottomHint>
      )}

      <BottomNav />
    </div>
  );
}

/**
 * Recap thumb — small overlapping circle showing input photo, or gradient
 * fallback if the object URL isn't available yet (race during state init).
 */
function RecapThumb({ url, fallbackHue }: { url: string | null; fallbackHue: number }) {
  return (
    <div
      style={{
        width: 40,
        height: 40,
        borderRadius: 999,
        border: `2px solid ${tg.bg2}`,
        overflow: 'hidden',
        background: url
          ? undefined
          : `linear-gradient(160deg, oklch(78% 0.12 ${fallbackHue}) 0%, oklch(58% 0.16 ${(fallbackHue + 30) % 360}) 100%)`,
        flexShrink: 0,
      }}
    >
      {url && (
        <img
          src={url}
          alt=""
          style={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover' }}
        />
      )}
    </div>
  );
}
